import React, { useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { ChevronDown, Check, FolderKanban, Search } from "lucide-react";
import { cn } from "../../lib/utils";
import { PROJECT_STATUS_UK, isProjectDone } from "./format";
import { tr } from "./i18n";

/**
 * Compact project picker (replaces the scrolling tab strip): one button with
 * the current project; the list shows status and a dot where money is due
 * (red = overdue, amber = invoiced) or something waits on the client.
 */
const ProjectPicker = ({ projects = [], selected, onSelect, flagOf, compact = false }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const active = projects.filter((p) => !isProjectDone(p));
  const done = projects.filter(isProjectDone);
  const match = (p) => !q || (p.name || "").toLowerCase().includes(q.toLowerCase());
  const Row = (p) => {
    const flag = flagOf(p);
    const isSel = p.id === selected?.id;
    return (
      <button key={p.id} type="button"
        onClick={() => { onSelect(p.id); setOpen(false); setQ(""); }}
        className={cn("w-full flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors",
          isSel ? "bg-orange-500/10 text-zinc-50" : "text-zinc-300 hover:bg-zinc-800")}>
        <span className={cn("w-2 h-2 rounded-full flex-shrink-0", flag || "bg-transparent")} />
        <span className="truncate flex-1">{p.name}</span>
        <span className="text-[11px] text-zinc-500 whitespace-nowrap">{tr(PROJECT_STATUS_UK[p.status] || "")}</span>
        {isSel && <Check className="h-3.5 w-3.5 text-orange-500 flex-shrink-0" />}
      </button>
    );
  };
  const otherFlags = projects.filter((p) => p.id !== selected?.id && ["bg-red-500", "bg-amber-500"].includes(flagOf(p))).length;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="client-project-switcher"
          className={cn("group inline-flex max-w-full items-center gap-2.5 rounded-xl border border-zinc-800 bg-zinc-900/60 px-3.5 py-2 text-left hover:border-zinc-600 transition-colors", compact && "w-full px-3")}>
          <FolderKanban className="h-4 w-4 text-orange-500 flex-shrink-0" />
          <span className="min-w-0">
            <span className="block text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">
              {tr("Проєкт")}
            </span>
            <span className={cn("block truncate text-sm font-semibold text-zinc-50", compact ? "max-w-[150px]" : "max-w-[420px]")}>{selected?.name || "—"}</span>
          </span>
          {otherFlags > 0 && !compact && (
            <span className="ml-1 rounded-md bg-red-500/15 px-1.5 py-0.5 text-[11px] font-semibold text-red-500 whitespace-nowrap">
              {tr("ще")} {otherFlags} {tr("з оплатою")}
            </span>
          )}
          {otherFlags > 0 && compact && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title={`${tr("ще")} ${otherFlags} ${tr("з оплатою")}`} />}
          <ChevronDown className="h-4 w-4 text-zinc-500 group-hover:text-zinc-200 flex-shrink-0 ml-auto" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[440px] max-w-[92vw] p-2 bg-zinc-900 border-zinc-800">
        {projects.length > 6 && (
          <div className="flex items-center gap-2 rounded-lg border border-zinc-800 px-2.5 py-1.5 mb-2">
            <Search className="h-3.5 w-3.5 text-zinc-500" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={tr("Пошук проєкту")}
              className="w-full bg-transparent text-sm text-zinc-100 outline-none placeholder:text-zinc-600" />
          </div>
        )}
        <div className="max-h-[360px] overflow-y-auto space-y-0.5">
          {active.filter(match).length > 0 && (
            <p className="px-2.5 pt-1 pb-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{tr("Активні")}</p>
          )}
          {active.filter(match).map(Row)}
          {done.filter(match).length > 0 && (
            <p className="px-2.5 pt-3 pb-1 text-[10px] uppercase tracking-[0.12em] text-zinc-500 font-semibold">{tr("Завершені")}</p>
          )}
          {done.filter(match).map(Row)}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ProjectPicker;
